<template>
    <v-dialog v-model="show" no-click-animation persistent width="500">
        <v-card>
            <v-layout class="headline grey lighten-2 pl-3 pr-2 py-2" primary-title align-center>
                <div>{{ $t('editor.sample') }}</div>
                <v-spacer></v-spacer>
                <v-btn small icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-layout>
            <v-card-text style="height: 700px">
                <v-form ref="form" @submit="submit" v-model="formValid">
                    <v-text-field
                        :label="$t('content') | capitalize"
                        v-model="content"
                        :rules="formRules.required"
                    ></v-text-field>
                    <v-layout>
                        <v-autocomplete
                            :label="$t('intent') | capitalize"
                            v-model="intent_code"
                            item-text="label"
                            item-value="code"
                            :items="intentList"
                            :rules="formRules.required"
                        ></v-autocomplete>
                        <v-btn class="mr-0" icon @click="addIntent">
                            <v-icon>mdi-plus</v-icon>
                        </v-btn>
                    </v-layout>
                    <div class="centerXY">1. Select keyword</div>
                    <div class="selection-container">
                        <!-- eslint-disable vue/no-v-html -->
                        <div v-html="contentHighlight" class="selection-tag"></div>
                        <!--eslint-enable-->
                        <div @mouseup="checkSelection" class="selection-content">{{content}}</div>
                    </div>
                    <div class="centerXY">2. Select entity type</div>
                    <template v-for="(selection, i) in selectionList">
                        <v-layout :key="i" align-center v-if="!selection.deleted">
                            <v-text-field
                                v-model="selection.value"
                                class="mr-3"
                                disabled
                            ></v-text-field>
                            <v-autocomplete
                                v-model="selection.entity_code"
                                :items="entityList"
                                item-text="label"
                                item-value="code"
                                :rules="formRules.required"
                            ></v-autocomplete>
                            <v-btn icon @click="deleteSelection(i)"><v-icon>mdi-delete</v-icon></v-btn>
                        </v-layout>
                    </template>
                    <v-layout>
                        <v-spacer></v-spacer>
                        <v-btn class="ma-0" type="submit">{{ $t('save') }}</v-btn>
                    </v-layout>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import DialogPromiseFormMixin from "@/modules/fptai/mixins/DialogPromiseFormMixin";
import NLP_Service from "@/services/nlp.service";
import { ScenarioEventBus, ScenarioEventBusType } from "@/views/Scenario/scenario.eventbus";

export default {
    name: "SampleEditor",
    mixins: [DialogPromiseFormMixin],
    data () {
        return {
            item: null,
            content: '',
            intent_code: '',
            selectionList: [],
            originalSelectionListMap: {}
        }
    },
    computed: {
        ...mapState({
            intentList: state => state.scenario.intentList,
            entityList: state => state.scenario.entityList,
        }),
        contentHighlight () {
            if (this.content && this.selectionList.length > 0) {
                let selectionList = this.selectionList.filter(item => !item.deleted)
                if (selectionList.length < 1) {
                    return this.content
                }
                selectionList = this.$lodash.orderBy(selectionList, item => item.start)
                let content = this.content
                let str = this.decodeWhiteSpace(content.substring(0, selectionList[0].start))
                for (let i = 0; i < selectionList.length; i++) {
                    let selection = selectionList[i]
                    if (selection.deleted) {
                        continue
                    }
                    let selectionContent = this.decodeWhiteSpace(content.substring(selection.start, selection.end + 1))
                    str += `<span class="selection-highlight">${selectionContent}</span>`
                    if (i < selectionList.length - 1) {
                        let selectionNext = selectionList[i + 1]
                        str += this.decodeWhiteSpace(content.substring(selection.end + 1, selectionNext.start))
                    }
                }
                str += this.decodeWhiteSpace(
                    content.substring(selectionList[selectionList.length - 1].end + 1, content.length)
                )
                return str
            }
            return this.content
        }
    },
    methods: {
        open (item) {
            this.clearForm()
            if (item) {
                if (item.code) {
                    NLP_Service.SampleService.read(this.$route.params.scenarioId, item.code).then(
                        result => {
                            this.item = result.data
                            this.content = result.data.content
                            this.intent_code = result.data.intent.intent_code
                            this.selectionList = result.data.entities
                            for (let selection of this.selectionList) {
                                this.originalSelectionListMap[selection.entity_sample_code] = { ...selection }
                            }
                        }
                    )
                } else {
                    this.content = item.content || ''
                    this.intent_code = item.intent_code || ''
                }
            }
            this.show = true
            return new Promise((resolve, reject) => {
                this.promise = { resolve, reject }
            })
        },
        checkSelection () {
            if (!window.getSelection) {
                return
            }
            let selection = window.getSelection()
            if (selection.anchorOffset === selection.focusOffset) {
                return
            }
            let start, end
            if (selection.anchorOffset < selection.focusOffset) {
                start = selection.anchorOffset
                end = selection.focusOffset - 1
            } else {
                start = selection.focusOffset
                end = selection.anchorOffset - 1
            }
            let selectionList = this.selectionList.filter(item => !item.deleted)
            selectionList = this.$lodash.orderBy(selectionList, item => item.start)
            let overlap = false
            for (let selection of selectionList) {
                if (
                    (selection.start <= start && start <= selection.end) ||
                    (selection.start <= end && end <= selection.end)
                ) {
                    overlap = true
                    break
                }
            }
            selection.empty()
            if (overlap) {
                return
            }
            let content = this.content
            // remove start with white space
            while (content.substring(start, start + 1) === ' ') {
                start++
            }
            // change start to word beginning
            while (start > 0 && content.substring(start - 1, start) !== ' ') {
                start--
            }
            // remove end with white space
            while (content.substring(end, end + 1) === ' ') {
                end--
            }
            // change end to word ending
            while (end + 1 < content.length && content.substring(end + 1, end + 2) !== ' ') {
                end++
            }
            this.selectionList.push({
                entity_code: '',
                sample_code: this.item ? this.item.code : '',
                parent_code: '',
                value: content.substring(start, end + 1),
                start,
                end
            })
        },
        deleteSelection (index) {
            this.selectionList[index].deleted = true
            this.selectionList = [...this.selectionList]
        },
        decodeWhiteSpace (str) {
            while (str.startsWith(' ')) {
                str = '&nbsp;' + str.substring(1)
            }
            while (str.endsWith(' ')) {
                str = str.substring(0, str.length - 1) + '&nbsp;'
            }
            return str
        },
        submitValidForm () {
            if (this.item) {
                this.updateSample()
            } else {
                this.createSample()
            }
            this.close()
        },
        createSample () {
            let scenarioId = this.$route.params.scenarioId
            this.$appService.showLoading()
            NLP_Service.SampleService.create(
                scenarioId,
                { content: this.content }
            ).then(
                result => {
                    this.$appService.handleRequestSuccess(
                        this.$t('actionSuccessful', {
                            item: this.$t('sample'),
                            action: this.$t('create')
                        })
                    )
                    return Promise.resolve(result.data)
                },
                error => {
                    this.$appService.hideLoading()
                    this.$appService.handleRequestError()(error)
                }
            ).then(
                sample => {
                    let count = this.selectionList.length + 1;
                    let finallyFn = () => {
                        count--
                        if (count === 0) {
                            this.$appService.hideLoading()
                            this.promise.resolve()
                        }
                    }

                    // update intent to created sample
                    NLP_Service.IntentSampleService.create(scenarioId, {
                        intent_code: this.intent_code,
                        sample_code: sample.code
                    }).finally(finallyFn)

                    // update entities to created sample
                    for (let selection of this.selectionList) {
                        selection.sample_code = sample.code
                        NLP_Service.EntitySampleService.create(scenarioId, selection).then(
                            null,
                            this.$appService.handleRequestError()
                        ).finally(finallyFn)
                    }
                },
                this.$appService.handleRequestError()
            )
        },
        updateSample () {
            let scenarioId = this.$route.params.scenarioId
            let count = 0;
            let finallyFn = () => {
                count--
                if (count === 0) {
                    this.$appService.hideLoading()
                    this.promise.resolve()
                }
            }
            if (this.item.content !== this.content) {
                count++
                NLP_Service.SampleService.replace(scenarioId, this.item.code, { content: this.content }).then(
                    null,
                    this.$appService.handleRequestError()
                ).finally(finallyFn)
            }
            if (this.item.intent.intent_code !== this.intent_code) {
                count++
                NLP_Service.IntentSampleService.create(scenarioId, {
                    intent_code: this.intent_code,
                    sample_code: this.item.code
                }).then(
                    null,
                    this.$appService.handleRequestError()
                ).finally(finallyFn)
            }
            for (let selection of this.selectionList) {
                if (selection.deleted) {
                    count++
                    NLP_Service.EntitySampleService.delete(
                        this.$route.params.scenarioId, selection.entity_sample_code
                    ).then(
                        null,
                        this.$appService.handleRequestError()
                    ).finally(finallyFn)
                } else {
                    let shouldAdd = false
                    if (selection.entity_sample_code) {
                        let original = this.originalSelectionListMap[selection.entity_sample_code]
                        if (JSON.stringify(original) !== JSON.stringify(selection)) {
                            shouldAdd = true
                        }
                    } else {
                        shouldAdd = true
                    }
                    if (shouldAdd) {
                        count++
                        NLP_Service.EntitySampleService.create(this.$route.params.scenarioId, selection).then(
                            null,
                            this.$appService.handleRequestError()
                        ).finally(finallyFn)
                    }
                }
            }
            if (count === 0) {
                this.promise.resolve()
            }
        },
        addIntent () {
            ScenarioEventBus.$emit(ScenarioEventBusType.CREATE_INTENT, {
                item: null,
                resolve: item => {
                    this.$store.dispatch(
                        this.$actionTypes.scenario.GET_INTENT_LIST,
                        this.$route.params.scenarioId
                    ).then(() => {
                        this.intent_code = item.code
                    })
                }
            })
        }
    }
}
</script>

<style scoped lang="scss">
    .selection-container {
        min-height: 30px;
        background: #fff;
        border: 1px solid #EDEDF2;
        position: relative;

        .selection-tag {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1;
        }

        .selection-content {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2;
        }
    }

    ::v-deep .selection-highlight {
        background-color: lightgray;
    }
</style>
